import React, { useEffect, useRef, useState } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { useParams } from 'react-router';
import { useAtom } from 'jotai';
import { useDispatch } from 'react-redux';
import produce from 'immer';
import classnames from 'classnames';
import { _isEmpty as _isEmptyObj } from '@/utils/jsHelpers/obj';
// components
import {
  ForwardArrow,
  ArrowLeft,
  LongDashIcon,
} from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
import FormSection from '@/components/forms/FormSection';
import PromptMessage, {
  getFormSectionFields,
} from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/CustomFields';
import GetDisclaimerMessage from '@/components/landing-page-templates/ConfigLandingPage/ConfigAuthComponents/AuthForms/DisclaimerMessage';
import Text from '@/components/ui/content/Text';
// config
import { NAME_FORM_FIELDS, formatCustomFieldValues } from './config';
// hooks/utils
import useRegistrationConditionFieldShowValidation from '@/hooks/use-registration-condition-field-show-validation';
// api
import api from '@/api';
// types
import { IFormFieldCategory, IFormFieldRow } from '@/types/form';
import { IAccountRegistrationEnum } from '@/models/account/types';
// styles
import styles from './styles.module.scss';
// utils
import { _isEmpty, _isUndefined } from '@/utils/jsHelpers/obj';
import { ILandingPage, IRegistrationPageSource } from '../../types';
import {
  getUtmJsonFromUrl,
  getRichTextFromHtmlString,
  validateEmailForRegistration,
  scrollToUnfilledFields,
  isRegistrationInActive,
} from '@/utils/helpers';
import { IRegistrationModel } from '@/models/event/types';
import { InfoCircle } from '@styled-icons/boxicons-solid';
import { unfilledRegistrationFieldRefAtom } from '@/components/ui/NewFormComponents/LandingPageInput/atoms';
import { EEventType } from '@/types/event';
import Modal from '@/components/ui/modals/Modal';
import { IModalTypes } from '@/components/ui/modals/Modal/types';
import Button from '@/components/ui/buttons/Button';
import RegistrationsEnded from '@/components/landing-page-templates/ConfigurableLandingPage/LandingPageCommonComponents/RegistrationEnded';

const RegistrationCustomFields = ({
  event,
  landingPageObject,
  currentUser,
  currentUserEventPermissions,
  setCheckRegistrationExists,
  email,
  shouldUpdateCustomField = false,
  isEmbedView = false,
  isSpeakerPage,
  setShowLoginOption,
  setGuestAuthState,
  guestModeEnabled,
  isGuestLogin = false,
  onlyGuestAuthIsEnabled = false,
  setIsRegisteredOrAlreadyRegistered,
  showLoginButton = false,
  isEventBefore = false,
  isSeriesEvent = false,
}) => {
  const {
    eventLandingPageId,
    formBgColour,
    formFontColour,
    registrationCustomFieldList,
    isPromptRequired,
    isDisclaimerRequired,
    disclaimerMessage,
    promptMessage,
    magicLinkEnabled,
    emailRestrictionType,
    restrictedEmailDomainsList,
    landingPageTemplateName,
  } = landingPageObject;

  const dispatch = useDispatch();
  const { eventId } = useParams() as any;
  const [subScription, setSubScription] = useState('YES');
  const [mandatoryDisclaimerErrors, setMandatoryDisclaimerErrors] = useState(
    {},
  );
  const organizationId = event?.organizationId;
  const [eventDisclaimers, setEventDisclaimers] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [showRegistrationFullPopup, setShowRegistrationFullPopup] = useState(false);
  const [registrationFieldRef, setRegistrationFieldRef] = useAtom(
    unfilledRegistrationFieldRefAtom,
  );

  const formFieldRef = useRef<HTMLDivElement>(null);
  const disclaimerRef = useRef<HTMLDivElement>(null);

  const [hasRegistered, setHasRegistered] = useState(false);
  const [hasAlreadyRegistered, setHasAlreadyRegistered] = useState(false);
  const [isRegisterApiLoading, setIsRegisterApiLoading] = useState(false);

  const showRegistrationForm =
    event.type === 'SERIES' ||
    event?.isRegistrationsEnabled ||
    shouldUpdateCustomField ||
    isGuestLogin;

  const [initialDetails, setInitialDetails] = useState() as any;

  const isSponsorLandingPage = landingPageTemplateName === ILandingPage.SPONSOR;

  let initialValues = {
    firstName: currentUser.firstName === 'Event' ? '' : currentUser.firstName,
    lastName: currentUser.lastName === 'Attendee' ? '' : currentUser.lastName,
    email: email ? email : currentUser?.email ? currentUser?.email : '',
    phoneNumber: currentUser.phoneNumber ? currentUser.phoneNumber : '',
    designation: currentUser.designation ? currentUser.designation : '',
    bio: currentUser.bio ? currentUser.bio : '',
    company: currentUser.company ? currentUser.company : '',
    country: currentUser.country ? currentUser.country : '',
  };
  const isWebinar = event?.type === EEventType.WEBINAR;

  useEffect(() => {
    api.event.getCountry().then(res => {
      setCountryList(res.data);
    });
  }, []);

  useEffect(() => {
    if (setIsRegisteredOrAlreadyRegistered) {
      setIsRegisteredOrAlreadyRegistered(hasRegistered || hasAlreadyRegistered);
    }
  }, [hasRegistered, hasAlreadyRegistered, setIsRegisteredOrAlreadyRegistered])

  useEffect(() => {
    if (currentUserEventPermissions.customField) {
      let customFieldValues = formatCustomFieldValues(
        currentUserEventPermissions.customField,
        registrationCustomFieldList,
      );
      initialValues = {
        ...initialValues,
        ...customFieldValues,
      };
    }
    setInitialDetails(initialValues);
  }, [currentUserEventPermissions]);

  const [formValues, setFormValues] = useState() as any;
  const { customFields } = useRegistrationConditionFieldShowValidation(
    formValues,
    registrationCustomFieldList,
  );

  useEffect(() => {
    if (!eventId || !eventLandingPageId) return;

    api.event
      .fetchEventDisclaimers(eventId, eventLandingPageId)
      .then(({ data }) => {
        setEventDisclaimers(
          data.map(each => {
            return {
              ...each,
              isConsentGiven: false,
            };
          }),
        );
      })
      .catch(err => console.error(err));
  }, [eventId, eventLandingPageId]);

  // Submit Button styles
  const buttonStyles = {
    backgroundColor: formBgColour === '#000000' ? '#353535' : formBgColour,
    color: formFontColour,
  };

  const mandatoryDisclaimerCheck = (meta, name, text) => {
    try {
      if (!mandatoryDisclaimerErrors[name] && meta.touched && meta.error) {
        setMandatoryDisclaimerErrors({
          ...mandatoryDisclaimerErrors,
          [name]: text,
        });
      } else if (
        !!mandatoryDisclaimerErrors[name] &&
        meta.touched &&
        !meta.error
      ) {
        const { [name]: value, ...rest } = mandatoryDisclaimerErrors;
        setMandatoryDisclaimerErrors(rest);
      }
    } catch (e) {
      console.error('error in mandatory disclaimer check', e);
    }
  };

  // from submission here
  const submitHandler = async values => {
    setIsRegisterApiLoading(true);
    setCheckRegistrationExists(true);

    const customData = {} as any;
    if (isPromptRequired) {
      customData.subScription = subScription;
    }
    if (customFields && customFields.length > 0) {
      customFields.forEach(element => {
        if (!element.isPredefinedField) {
          customData[element.labelName] = values[element.labelName];
        }

        // set seriesEventSelection [] if SELECT_ALL is selected
        if (
          element.fieldCategory === IFormFieldCategory.EVENT_SELECTION &&
          customData[element.labelName]?.includes('SELECT_ALL')
        ) {
          customData[element.labelName] = [];
        }
      });
    }

    customData.disclaimers = [];
    eventDisclaimers.forEach(element => {
      if (element.requestConsent) {
        customData.disclaimers.push({
          text: element.text,
          consentGiven: element.isConsentGiven,
        });
      }
    });
    // Set all the custom fields
    // values.customField = JSON.stringify(customData);
    const customFieldDetails = JSON.stringify(customData);

    const registrationDetailsObject = {
      eventId,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      bio: values.bio ? values.bio : null,
      designation: values.designation ? values.designation : null,
      company: values.company ? values.company : null,
      phoneNumber: values.phoneNumber ? values.phoneNumber : null,
      country: values.country ? values.country : null,
      customField: customFieldDetails,
      source: isEmbedView
        ? IRegistrationPageSource.DUDA_REGISTRATION_FORM
        : IRegistrationPageSource.LANDING_PAGE_V2,
      utm: getUtmJsonFromUrl(),
    } as IRegistrationModel;
    console.warn(
      'RegistrationCustomFields submitHandler isEmbedView---',
      isEmbedView,
    );

    if(isGuestLogin) {
      const { status, data } = await api.account.createGuestAuthSession(registrationDetailsObject, eventId);
      if (status === 200) {
        dispatch({
          type: 'account/setAccountAndRegistrationStep',
          payload: {
            userData: data,
            registrationStep: IAccountRegistrationEnum.COMPLETE,
          },
        });
      } else {
        dispatch({
          type: 'global/addDangerToast',
          payload: { description: 'There was an error logging you in' },
        });
      }
      setIsRegisterApiLoading(false);
      return;
    }

    if (shouldUpdateCustomField) {
      const registrationRequestObject = {
        eventId,
        email: values.email,
      };
      api.registration
        .getRegistrationByEmail(registrationRequestObject)
        .then(regRes => {
          if (regRes.data) {
            const { registrationId } = regRes.data;
            delete registrationDetailsObject.ticketTypeId; // on update no need to pass ticketTypeId
            api.registration
              .updateRegistration(
                eventId,
                registrationId,
                registrationDetailsObject,
              )
              .then(() => {
                window.location.reload();
              })
              .catch(e => {
                if (e.response.status !== 200) {
                  dispatch({
                    type: 'global/addDangerToast',
                    payload: { description: e.response.data.description },
                  });
                }
              });
          }
        });
      setIsRegisterApiLoading(false);
      return;
    }

    api.registration
      .saveRegistration(registrationDetailsObject)
      .then(() => {
        setHasRegistered(true);
        setIsRegisterApiLoading(false);
      })
      .catch(e => {
        setIsRegisterApiLoading(false);
        if (e.response.status !== 200) {
          if (e.response.data.message === 'ALREADY_REGISTERED') {
            setHasRegistered(true);
            setHasAlreadyRegistered(true);
          } else if (isRegistrationInActive(e?.response?.data?.message)) {
            dispatch({
              type: 'global/addDangerToast',
              payload: { description: e.response.data.description },
            });
          } else if (e.response.data.message === 'RESTRICTED_PLAN') {
            setShowRegistrationFullPopup(true);
          }
        }
      });
  };

  // Dynamic form setup
  const formFields = [] as IFormFieldRow[];

  // Check if name is blank and add first and last name fields
  // Add the first and last name fields to the event registration form
  formFields.push(...NAME_FORM_FIELDS(shouldUpdateCustomField, organizationId));

  const updateFormDisclaimer = idx => {
    setEventDisclaimers(disclaimers =>
      produce(disclaimers, draft => {
        draft[idx].isConsentGiven = !draft[idx].isConsentGiven;
      }),
    );
  };

  const validateForm = values => {
    const errors = {} as any;
    const emailError = validateEmailForRegistration(
      values.email,
      restrictedEmailDomainsList,
      emailRestrictionType,
    );
    if (emailError) {
      errors.email = emailError;
    }

    const scrollFieldDetails = { 
      errors,
      formFieldRef,
      registrationFieldRef,
      mandatoryDisclaimerErrors,
      disclaimerRef,
    };

    scrollToUnfilledFields(scrollFieldDetails);

    return errors;
  };

  const formInputFields = getFormSectionFields(
    formFields,
    customFields,
    countryList,
  );

    // Set registration step back to initial step
    const goBackButtonHandler = () => {
      dispatch({
        type: 'account/setRegistrationStep',
        payload: { registrationStep: IAccountRegistrationEnum.LOGIN_METHOD },
      });
      dispatch({
        type: 'account/setSelectedLoginMethod',
        payload: { selectedLoginMethod: undefined },
      });
      setShowLoginOption && setShowLoginOption(false);
    };
    // The handler for a click on the back arrow
    const handleBackClick = () => {
      goBackButtonHandler();
    };

  useEffect(() => {
    // TODO: remove this after proper fix
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    window.__isSeriesEvent = isSeriesEvent;
  }, [isSeriesEvent]);

  return (
    <>
      <div className={styles.registrationCustomFieldContainerMain}>
        {isGuestLogin && <div>
          <div className={styles.sectionHeader}>
          {!onlyGuestAuthIsEnabled && <div
            role="button"
            tabIndex={0}
            className={styles.backArrow}
            onClick={handleBackClick}
          >
            <ArrowLeft />
          </div>}
            <p className={styles.sectionLabel}>Sign in as a Guest</p>
          </div>
          <span className={styles.sectionSubText}>
            Guest logins are not saved for the user.
            If you close this session and log back in, you won’t be able to access your data.
          </span>
        </div>}
        {hasRegistered && (
          <div className={styles.registrationMessageContainer}>
            {!hasAlreadyRegistered && (
              <div className={styles.registrationMessage}>
                You've successfully registered! <br />
                {magicLinkEnabled ? `Check your email for the event invite` : ``}
              </div>
            )}
            {hasAlreadyRegistered && (
              <div>
                <div className={styles.registrationMessageHead}>
                  <span
                    className={styles.registrationArrow}
                    onClick={() => {
                      setHasRegistered(false);
                      setHasAlreadyRegistered(false);
                    }}
                  >
                    <ArrowLeft />
                  </span>{' '}
                  You've already registered!
                </div>
                {magicLinkEnabled ? (
                  <div className={styles.registrationMessage}>
                    Please check the email invite <br /> to access this event
                  </div>
                ) : (
                  <div className={styles.registrationMessage}>
                    Please login <br /> to access this event
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!hasRegistered && (
          <Form
            onSubmit={submitHandler}
            initialValues={initialDetails}
            validate={values => validateForm(values)}
            render={({ handleSubmit, submitting, values }) => (
              <div>
                {showRegistrationForm && (
                  <>
                    <h2 className={styles.headline}>
                      <div className={styles.headlineText}>
                        {shouldUpdateCustomField
                          ? 'Please fill the required details'
                          : 'Your details'}
                      </div>
                    </h2>
                    <div className={styles.eventRegisterFormContainer}>
                      <FormSection
                        formData={formInputFields}
                        isSponsorLandingPage={isSponsorLandingPage}
                      />
                      <FormSpy
                        subscription={{ values: true }}
                        onChange={change => {
                          setFormValues(change.values);
                        }}
                      />
                    </div>
                    {eventDisclaimers && eventDisclaimers.length > 0 && (
                      <div className={styles.disclaimersSection}>
                        {eventDisclaimers.map((disclaimer, idx) => {
                          const {
                            text,
                            isConsentGiven,
                            requestConsent,
                            isConsentMandatory,
                          } = disclaimer;


                          const required = () => {
                            if (isConsentMandatory && !isConsentGiven) {
                              return 'This field is required';
                            }
                            return undefined;
                          };
                          return (
                            <div className={styles.disclaimerOuterContainer}>
                              {requestConsent ? (
                                <Field
                                  name={`label_${idx}`}
                                  validate={required}
                                  render={({ input, meta }) => {
                                    mandatoryDisclaimerCheck(
                                      meta,
                                      `label_${idx}`,
                                      text,
                                    );
                                    return (
                                      <div className={styles.checkBoxWrapper}>
                                        <input
                                          {...input}
                                          className={classnames(
                                            styles.styledCheckbox,
                                            {
                                              [styles.checkboxSponsorPage]: isSponsorLandingPage,
                                            },
                                          )}
                                          type="checkbox"
                                          checked={isConsentGiven}
                                          onChange={() => updateFormDisclaimer(idx)}
                                          id={`label${idx}`}
                                        />
                                        <label htmlFor={`label${idx}`}>
                                          <span
                                            className={classnames(
                                              styles.textAndAsteriskContainer,
                                              {
                                                [styles.requiredAsterisk]: isConsentMandatory,
                                              },
                                            )}
                                          >
                                            <Text
                                              text={getRichTextFromHtmlString(text)}
                                            />
                                          </span>
                                        </label>
                                      </div>
                                    );
                                  }}
                                />
                              ) : (
                                <>
                                  <label
                                    className={styles.disclaimerText}
                                    htmlFor={`label${idx}`}
                                  >
                                    <Text text={getRichTextFromHtmlString(text)} />
                                  </label>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!!Object.keys(mandatoryDisclaimerErrors).length && (
                      <div ref={disclaimerRef} className={styles.disclaimerWarning}>
                        <div className={styles.disclaimerWarningHeading}>
                          <InfoCircle className={styles.warningIcon} size={20} />
                          <p className={styles.mandatoryDisclaimerWarningText}>
                            Please accept these conditions to register:
                          </p>
                        </div>
                        <ul className={styles.disclaimerLabelWarning}>
                          {Object.keys(mandatoryDisclaimerErrors).map((name, idx) => (
                            <li key={`mandatory_${idx}`}>
                              <Text
                                text={getRichTextFromHtmlString(
                                  mandatoryDisclaimerErrors[name],
                                )}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {isPromptRequired && (
                      <PromptMessage
                        message={promptMessage}
                        setSubScription={setSubScription}
                      />
                    )}
                    {isDisclaimerRequired && (
                      <GetDisclaimerMessage message={disclaimerMessage} />
                    )}

                    <button
                      onClick={() => {
                        validateForm(values);
                        handleSubmit();
                      }}
                      style={buttonStyles}
                      className={styles.submitButton}
                      disabled={submitting || isRegisterApiLoading}
                      type="button"
                    >
                      <p className={styles.buttonText}>
                        {(shouldUpdateCustomField || isGuestLogin) ? 'Continue' : 'Register now'}
                      </p>
                      {/* Forward Arrow SVG */}
                      <ForwardArrow color={formFontColour} />
                    </button>
                  </>
                )}
                {isEventBefore && !showRegistrationForm && (
                  <RegistrationsEnded />
                )}
                {_isEmptyObj(currentUser) && event.type != 'SERIES' && (
                  <>
                  {!isGuestLogin && showLoginButton && <button
                      className={styles.alreadyRegisteredBtn}
                      onClick={() => setShowLoginOption(true)}
                    >
                      {!isSpeakerPage && 'Already Registered?'}
                      <span className={styles.alreadyRegisteredTextBold}>
                        <span className={styles.loginBtn}>
                          {isSpeakerPage ? 'Speaker Check-In' : 'Log in'}
                        </span>
                      </span>
                    </button>}
                    {guestModeEnabled && (
                      <div>
                        <div className={styles.continueWith}>
                          <LongDashIcon />
                          <LongDashIcon />
                          <LongDashIcon />
                          <LongDashIcon /> or <LongDashIcon />
                          <LongDashIcon />
                          <LongDashIcon />
                          <LongDashIcon />
                        </div>
                        <div
                          className={styles.guestLoginBtn}
                          onClick={() => setGuestAuthState()}
                        >
                          Login as a guest
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          />
        )}
      </div>
      {isWebinar && showRegistrationFullPopup &&
      <Modal
        title= "Registrations are full"
        styleClass={IModalTypes.NEUTRAL_LIGHTEST}
        autoTrigger={showRegistrationFullPopup}
        modalCloseClass={styles.crossIcon}
        className={styles.modalrenderContainer}
        isNewCrossIcon
        newCrossIconSize={20}
        handleCloseManually
        onModalClose={() => setShowRegistrationFullPopup(false)}
        render={() => (
          <div>
            <div className={styles.modalContainer}>
              Please contact the organizer to register for this event
            </div>
            <div className={styles.modalButtonContainer}>
              <Button
              type="button"
              onClick={() => setShowRegistrationFullPopup(false)}
              className={styles.modalConfirmButton}
            >
            Okay
            </Button></div>
            </div>
        )}
      />}
    </>
  );
};

export default RegistrationCustomFields;
